import _ from 'lodash'
import { updates, map, updateAt } from 'fn-update'
import { getJson, postJson, putJson, deleteJson } from './client'

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Auth
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const login = (body) => {
  return postJson('/login',{ raw:true, body }).then(result => {
    // Extract the API token from the headers and return alongside the user.
    const apiToken = result.headers.get('X-Api-Token')
    return result.json().then(user => ({ user, api_token: apiToken }))
  })
}

export const loginWithEmailPassword = ({email,password}) => {
  return login({ email, password })
}

export const loginWithMessenger = (signedRequest) => {
  const credentials = { signed_request: signedRequest }
  return login({ method: 'messenger', credentials })
}

export const refreshToken = () => postJson('/login/refresh', { raw:true })
  .then(result => result.json().then(user => ({
    user: user,
    api_token: result.headers.get('X-Api-Token')
  })))

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * User
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const putNINO = (nino) => {
  const body = { national_insurance_number:nino }
  return putJson('/user', { body })
}

export const putPhoneNumber = ({ country_code, number }) => {
  // Remove the first 0 if we received one
  const cleanNumber = number && number[0] === '0' ? number.slice(1) : number
  const body = { country_code, number: cleanNumber }
  return putJson('/phone_number', { body })
}

export const getAccount = () => {
  return getJson('/account')
}


export const createTermsAgreement = (version, term_type) => {
  const body = { version, term_type }
  return postJson('/terms_agreements', { body })
}

export const getUser = () => {
  return getJson('/v2/user');
}

export const getUserConfiguration = () => {
  return getJson('/v1/config');
}

export const updateUser = (payload) => {
  const body = payload
  return putJson('/user', { body });
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Institutions
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getInstitutions = () => {
  return getJson('/institutions')
}

export const getLinks = () => {
  return getJson('/links')
}

export const createInstitutionLink = (body) => {
  return postJson('/links', { body })
}

export const updateInstitutionLink = (payload) => {
  const body = payload.body
  const id = payload.id
  return putJson(`/links/${id}`, { body })
}

/* Only for Open Banking */

export const setLinkToUpgradingState = (id) => {
  const headers = {'X-Api-Version': '2019-08-26'}
  return putJson(`/v1/links/${id}/upgrade`, { headers })
}

/* --- */

export const getInstitutionLink = (payload) => {
  return getJson(`/links/${payload}`)
}

export const createFundingSource = (body) => {
  const headers = {'X-Api-Version': '2019-01-01'}
  return putJson('/funding_source', { body, headers })
}

export const getFundingSource = () => {
  return getJson('/funding_source')
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Terms
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const putInvestmentTermsAcceptance = () => {
  const body = { 'term_type': 'investment', 'version': '2018-02-20' }
  return postJson('/terms_agreements', { body })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Investment Eligibility
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getInvestmentEligibility = () => {
  return getJson('/investment_eligibility')
}

export const putInvestmentEligibility = (agreements) => {
  const body = { agreements }
  return putJson('/investment_eligibility', { body })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Investment Onboarding
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const createOnboarding = (flavor) => {
  const body = { flavor }
  return postJson('/v1/investment_onboardings', { body })
}

export const getOnboarding = (id) => {
  return getJson(`/v1/investment_onboardings/${id}`)
}

export const updateOnboarding = (id, data) => {
  const body = {}
  const { accountType, product, delayed } = data
  if (accountType !== undefined) body.account_type = accountType
  if (product !== undefined) body.product = product
  if (delayed !== undefined) body.delayed = delayed
  return putJson(`/v1/investment_onboardings/${id}`, { body })
}

export const confirmOnboarding = (id) => {
  return postJson(`/v1/investment_onboardings/${id}/confirm`)
}

export const completeOnboarding = (id) => {
  return postJson(`/v1/investment_onboardings/${id}/complete`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Address
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const toDate = (value) => value && new Date(value)

const toAddressModel = updates({
  residence_start_date: toDate,
  residence_end_date: toDate
})

export const createAddress = (address) => {
  return postJson('/addresses', { body:address }).then(toAddressModel)
}


// Used in oboarding to create the address

export const createInitialAddress = (address) => {
  return putJson('/address', { body:address })
}

export const updateAddress = (address) => {
  return putJson(`/addresses/${address.id}`,{body:address}).then(toAddressModel)
}

export const deleteAddress = (id) => {
  return deleteJson(`/addresses/${id}`).then(() => ({ id }))
}

export const getAddressHistory = () => {
  return getJson('/addresses').then(map(toAddressModel))
}

export const addressLookup = (postcode, country) => {
  const countryCode = country ? country : 'GB'
  return getJson(`/address/lookup?postal_code=${postcode}&country=${countryCode}`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Investment Product
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getProducts = () => {
  return getJson('/investment_products')
}

export const getProductPerformance = (product_id) => {
  return getJson(`/investment_products/${product_id}/performance`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Investment Portfolio
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getPortfolio = () => {
  return getJson('/portfolios/investment')
}

export const getInvestment = (id) => {
  return getJson(`/investments/${id}`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Feed
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getFeed = (id) => {
  return getJson(`/feeds/${id}`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Document
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getDocuments = () => {
  return getJson('/documents')
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * File
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const requestFileUpload = (fileType, contentType) => {
  return postJson('/file_uploads', {
    body: {
      file_type: fileType,
      content_type: contentType
    }
  })
}

export const createFileFromUpload = (fileUploadId) => {
  return postJson('/files', {
    body: {
      file_upload_id: fileUploadId
    }
  })
}

export const uploadToS3 = (url, s3UploadConfig, rawFile) => {
  const formData = new FormData()
  Object.keys(s3UploadConfig).forEach(k => {
    formData.append(k, s3UploadConfig[k])
  })
  formData.append('file', rawFile)

  return fetch(url, { method: 'POST', body: formData })
  .then(result => {
    // If all okay, no response is returned.
    if (!result.ok) throw Error(result.statusText)
  })
}

export const uploadFile = (fileType, rawFile, contentType) => {
  return requestFileUpload(fileType, contentType)
  .then(resp => {
    const fileUploadId = resp.id
    const p = uploadToS3(resp.url, resp.config, rawFile)
    return p.then(resp => createFileFromUpload(fileUploadId))
  })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Verification
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getVerification = (id) => {
  return getJson(`/verifications/${id}`)
}

export const submitComplianceDocuments = (documents) => {
  const body = _.map(documents, ({ documentType, documentTypeVariant, pages }) => {
    const data = { document_type: documentType, pages: pages }
    if (documentTypeVariant) data.document_type_variant = documentTypeVariant
    return data
  })
  return postJson('/compliance_documents', { body })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Regulars
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getRegulars = () => {
  return getJson(`/regulars`)
}

export const submitFeedbackForRegular = (regularId, note) => {
  return postJson(`/regulars/${regularId}/feedback`, { body: { note } })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Billing
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const withRealDates = map(
  updates([
    updateAt('invoices', map(updateAt('billing_date', toDate))),
    updateAt('next_billing_date', toDate),
  ]),
);

export const getPlanSubscriptions = () => {
  return getJson('/plan_subscriptions').then(withRealDates);
};

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Splitter
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const loadSplitter = () => {
  return getJson('/splitter')
}

export const saveSplitter = (splitter_id, splits, preset) => {
  return putJson(`/splitter/${splitter_id}`, { body: { splits, preset } })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Shawbrook
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const loadShawbrookOnboarding = () => {
  return getJson('/shawbrook_onboarding')
}

export const completeShawbrookOnboarding= (id, is_eligible) => {
  return putJson(`/shawbrook_onboarding/${id}/complete`, { body: { is_eligible } })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Switching
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getSwitchingRecommendations = (switchType) => {
  return getJson('/v1/recommendations', { qs: { 'switch_type': switchType } })
}

export const changeRecommendationParameters = (id, params) => {
  return postJson(`/v1/recommendations/${id}`, { body: { ...params } })
}

export const submitSwitch = (data) => {
  return postJson(`/v1/switches`, { body: { ...data } })
}

export const getSwitches = () => {
  return getJson('/v1/switches')
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Recommendations
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getAllRecommendations = () => {
  return getJson('/v1/recommendations')
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Support Heartbeat
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const postSupportHeartbeat = () => {
  return postJson(`/user/support-heartbeat`, {})
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Feature Flags
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getFeatureFlag = (name) => {
  return getJson(`/feature_flag/${name}`)
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Deposit / Transfer
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const depositFromBank = (data) => {
  return postJson(`/v1/deposits`, { body: { ...data } })
}

export const transferFromSavings = (data) => {
  return postJson(`/v1/transfers`, { body: { ...data } })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Savings
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getSaverById = (id) => {
  return getJson(`/v1/saver_run/${id}`)
}

export const getSavingRules = () => {
  return getJson(`/v1/saving_rules`)
}

export const unpauseSavingRule = (id) => {
  return putJson(`/v1/saving_rules/${id}`, { body: { paused: false } })
}

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Accounts
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

export const getAccountDefaults = () => {
  return getJson(`/v1/account_defaults`)
}

export const postNewAccount = (data) => {
  if (data.goal && data.goal.amount == 0) delete data.goal
  return postJson(`/v1/accounts`, { body: { ...data } })
}

export const getAccountFeed = (id) => {
  return getJson(`/v1/feed?account_id=${id}`)
}

export const getAccountBalances = (id) => {
  return getJson(`/v1/accounts/${id}`)
}



// WEBPACK FOOTER //
// ./src/api/api.js